import type { ThemeKey } from '../shared/themeKeys'
import type { AlbumConfig, AlbumRecord } from './domain'

export type {
  AlbumConfig,
  AlbumRecord,
  AppState,
  AlbumSize,
  AppTab,
  InvestmentDestination,
  StickerInvestment,
  ThemeDefinition,
  ThemeItem
} from './domain'

export interface PersistedState {
  albums: AlbumRecord[]
  activeAlbumId: string | null
  schemaVersion: number
}

export interface InvestAlbumApi {
  platform: string
  loadState: () => Promise<PersistedState>
  saveState: (state: PersistedState) => Promise<void>
  saveStateSync: (state: PersistedState) => void
  openExternalLink: (url: string) => Promise<boolean>
}

export const SCHEMA_VERSION = 2

export const LOAD_STATE_CHANNEL = 'persistence:load-state'
export const SAVE_STATE_CHANNEL = 'persistence:save-state'
export const SAVE_STATE_SYNC_CHANNEL = 'persistence:save-state-sync'
export const OPEN_EXTERNAL_LINK_CHANNEL = 'shell:open-external-link'

export function buildDefaultState(): PersistedState {
  return {
    albums: [],
    activeAlbumId: null,
    schemaVersion: SCHEMA_VERSION
  }
}

export function normalizeAlbumConfig(input: Partial<AlbumConfig> | null | undefined): AlbumConfig {
  const now = new Date().toISOString()
  return {
    id: input?.id ?? crypto.randomUUID(),
    name: input?.name ?? 'My Album',
    size: (input?.size as number) ?? 50,
    theme: (input?.theme as ThemeKey) ?? 'standard50',
    currency: input?.currency ?? 'USD',
    targetFinishDate: input?.targetFinishDate ?? '',
    savedInvestments: input?.savedInvestments ?? [],
    createdAt: input?.createdAt ?? now,
    updatedAt: input?.updatedAt ?? now
  }
}

export function normalizeAlbumRecord(input: Partial<AlbumRecord> | null | undefined): AlbumRecord {
  return {
    config: normalizeAlbumConfig(input?.config),
    stickers: input?.stickers ?? {}
  }
}

export function normalizeState(input: Partial<PersistedState> | null | undefined): PersistedState {
  const albums = (input?.albums ?? []).map(normalizeAlbumRecord)
  return {
    albums,
    activeAlbumId: input?.activeAlbumId ?? null,
    schemaVersion: SCHEMA_VERSION
  }
}
